import React, { useState, useEffect, useCallback, useMemo } from "react";
import brandingConfig from "../../../config/brandingConfig";
import {
  Plus,
  Receipt,
  UtensilsCrossed,
  MoreVertical,
  Edit2,
  Trash2,
  RefreshCw,
  X,
  Save,
  Star,
  Loader2,
  Power,
  Circle,
  Copy,
  Eye,
  EyeOff,
  FileText,
  Settings,
  Type,
  AlignLeft,
  AlignCenter,
  Hash,
  Phone,
  MapPin,
  Building2,
  CreditCard,
  QrCode,
  MessageSquare,
  Clock,
  User,
  ChefHat,
  Utensils,
  BadgePercent,
  IndianRupee,
  Printer,
} from "lucide-react";
import receiptConfigurationService from "../../../services/receiptConfigurationService";
import printService from "../../../services/printService";
import printerService from "../../../services/printerService";
import branchService from "../../../services/branchService";
import toast from "react-hot-toast";

// ============================================================
// Tab Button Component - Trendy Design
// ============================================================
function TabButton({ active, onClick, icon: Icon, label, count, activeClass, iconClass, badgeClass }) {
  return (
    <button
      onClick={onClick}
      className={`relative px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
        active
          ? `${activeClass} shadow-sm`
          : "text-slate-500 hover:text-slate-700 hover:bg-white/50"
      }`}
    >
      <Icon size={16} className={active ? iconClass : "text-slate-400"} />
      <span>{label}</span>
      {count > 0 && (
        <span className={`ml-1 px-1.5 py-0.5 text-[10px] font-semibold rounded-full ${
          active ? badgeClass : "bg-slate-200 text-slate-600"
        }`}>
          {count}
        </span>
      )}
      {active && (
        <span className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-1 h-1 bg-current rounded-full"></span>
      )}
    </button>
  );
}

// ============================================================
// Generate Formatted Content for Printing
// ============================================================
function generateFormattedContent(config, branchDetails = null) {
  const isBill = config.receiptType === "BILL";
  const isKOT = config.receiptType === "KOT" || config.receiptType === "CANCEL_KOT";
  const isCancelKOT = config.receiptType === "CANCEL_KOT";
  const width = config.paperWidth === "58mm" ? 32 : 48;

  const line = "=".repeat(width);
  const dashed = "-".repeat(width);
  const center = (text) => {
    const padding = Math.max(0, Math.floor((width - text.length) / 2));
    return " ".repeat(padding) + text;
  };
  const twoCol = (left, right) => {
    const space = Math.max(1, width - left.length - right.length);
    return left + " ".repeat(space) + right;
  };

  let content = [];

  // Header - Use branch details if available
  if (config.showRestaurantName) content.push(center(branchDetails?.name || "MAVERICK DINE"));
  if (config.showAddress && branchDetails?.address) content.push(center(branchDetails.address));
  if (config.showPhone && branchDetails?.phone) content.push(center(`Ph: ${branchDetails.phone}`));
  if (config.showGstin && branchDetails?.gstNumber) content.push(center(`GSTIN: ${branchDetails.gstNumber}`));
  if (config.showFssai && branchDetails?.fssaiNumber) content.push(center(`FSSAI: ${branchDetails.fssaiNumber}`));
  if (config.headerText) content.push(center(config.headerText));
  content.push("");

  if (isKOT) {
    content.push(center(isCancelKOT ? "*** CANCEL KOT ***" : "KITCHEN ORDER TICKET"));
    if (isCancelKOT) content.push(center("*** CANCELLED ***"));
    content.push("");
    if (config.showKitchenStation) content.push(center("[MAIN KITCHEN]"));
    if (config.showOrderNumber) content.push(center("#KOT-TEST-001"));
    content.push(dashed);
    if (config.showTableNumber) content.push(twoCol("Table:", "T5 (A)"));
    if (config.showWaiterName) content.push(twoCol("Waiter:", "John"));
    if (config.showTimestamp) content.push(twoCol("Time:", new Date().toLocaleString()));
    if (isCancelKOT && config.showSpecialInstructions) {
      content.push(dashed);
      content.push("REASON: Customer changed mind");
    }
    content.push(dashed);
    content.push(twoCol(isCancelKOT ? "ITEMS TO CANCEL" : "Item", "Qty"));
    content.push(dashed);

    const items = [
      { name: "Chicken Biryani", qty: 2 },
      { name: "Butter Naan", qty: 4 },
      { name: "Paneer Tikka", qty: 1 },
    ];
    items.forEach(item => {
      content.push(twoCol(item.name, String(item.qty)));
    });

    if (config.showSpecialInstructions && !isCancelKOT) {
      content.push(dashed);
      content.push("Note: Extra spicy, no onions");
    }

    if (isCancelKOT) {
      content.push(line);
      content.push(center("DO NOT PREPARE"));
      content.push(center("If prepared, discard"));
    }

    content.push(dashed);
    content.push(center("Total Items: 7"));
  } else {
    content.push(center("Bill #: TEST-" + Date.now()));
    content.push(center("Table: T5 | " + new Date().toLocaleDateString()));
    if (config.showCustomerInfo) {
      content.push(dashed);
      content.push("CUSTOMER");
      content.push("Name      : John Doe");
      content.push("Phone     : 9876543210");
    }
    content.push(dashed);

    const itemHeader = config.showQuantity && config.showUnitPrice
      ? "Item                    Qty    Amount"
      : config.showQuantity ? "Item                         Qty" : "Item";
    content.push(itemHeader.substring(0, width));
    content.push(dashed);

    const items = [
      { name: "Chicken Biryani", qty: 2, price: 350, total: 700 },
      { name: "Butter Naan", qty: 4, price: 40, total: 160 },
      { name: "Paneer Tikka", qty: 1, price: 280, total: 280 },
    ];
    items.forEach(item => {
      if (config.showItemDescription) {
        let ln = item.name;
        if (config.showQuantity) ln = twoCol(ln, String(item.qty));
        if (config.showUnitPrice) ln = twoCol(item.name, `${item.qty}    ${item.total.toFixed(2)}`);
        content.push(ln.substring(0, width));
        if (config.showUnitPrice) content.push(`  @ ${item.price.toFixed(2)}`);
      }
    });

    content.push(dashed);

    if (config.showSubtotal) content.push(twoCol("Subtotal:", "1,140.00"));
    if (config.showDiscountTotal) content.push(twoCol("Discount (10%):", "-114.00"));
    if (config.showTaxBreakdown) {
      content.push(twoCol("CGST (2.5%):", "25.65"));
      content.push(twoCol("SGST (2.5%):", "25.65"));
    }
    content.push(twoCol("Round Off:", "-0.30"));

    if (config.showGrandTotal) {
      content.push(line);
      content.push(twoCol("*** GRAND TOTAL ***", "1,077.00"));
      content.push(line);
    }

    if (config.showPaymentMethod) content.push(twoCol("Payment:", "CASH"));

    if (config.showQrCode) {
      content.push("");
      content.push(center("[QR CODE]"));
      content.push(center("Scan to Pay"));
    }

    if (config.showThankYou) {
      content.push(dashed);
      content.push(center(config.thankYouText || branchDetails?.thankYouMessage || "Thank you! Visit Again!"));
    }
  }

  if (config.footerText) content.push(center(config.footerText));
  content.push("");
  content.push(center(brandingConfig.printerPoweredBy));
  content.push("");
  content.push("");

  return content.join("\n");
}

// Receipt Type Colors
const RECEIPT_TYPE_COLORS = {
  BILL: { bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200", gradient: "from-blue-500 to-blue-600", label: "Bill Receipt" },
  KOT: { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200", gradient: "from-amber-500 to-orange-500", label: "KOT" },
  CANCEL_KOT: { bg: "bg-red-50", text: "text-red-700", border: "border-red-200", gradient: "from-red-500 to-rose-600", label: "Cancel KOT" },
};

// Default configuration template
const DEFAULT_CONFIG = {
  name: "",
  description: "",
  receiptType: "BILL",
  paperWidth: "80mm",
  isDefault: false,
  isActive: true,
  showLogo: true,
  showRestaurantName: true,
  showAddress: true,
  showPhone: true,
  showGstin: true,
  showFssai: false,
  headerText: "",
  showItemCode: false,
  showItemDescription: true,
  showQuantity: true,
  showUnitPrice: true,
  showItemTax: false,
  showItemDiscount: true,
  showSubtotal: true,
  showTaxBreakdown: true,
  showDiscountTotal: true,
  showGrandTotal: true,
  showPaymentMethod: true,
  showThankYou: true,
  thankYouText: "Thank you! Visit Again!",
  footerText: "",
  showQrCode: false,
  qrCodeData: "",
  showOrderNumber: true,
  showTableNumber: true,
  showWaiterName: true,
  showCustomerInfo: true,
  showTimestamp: true,
  showKitchenStation: true,
  showSpecialInstructions: true,
  kotFontSize: "normal",
};

export default function ReceiptConfigurations({ renderActions }) {
  const [configurations, setConfigurations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("BILL");
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [openActionMenu, setOpenActionMenu] = useState(null);
  const [branchDetails, setBranchDetails] = useState(null);
  const [selectedBranchId, setSelectedBranchId] = useState(null);

  // Derive branches from configurations data
  const branches = useMemo(() => {
    const map = new Map();
    configurations.forEach(c => {
      if (c.branchId && !map.has(c.branchId)) {
        map.set(c.branchId, { id: c.branchId, name: c.branchName });
      }
    });
    const list = Array.from(map.values());
    return list.length > 1 ? list : [];
  }, [configurations]);

  // Auto-select first branch when branches change
  useEffect(() => {
    if (branches.length > 1 && !selectedBranchId) {
      setSelectedBranchId(branches[0].id);
    }
    if (branches.length <= 1) {
      setSelectedBranchId(null);
    }
  }, [branches]);

  // Branch-filtered configurations
  const branchConfigs = useMemo(() => {
    if (!selectedBranchId) return configurations;
    return configurations.filter(c => c.branchId === selectedBranchId);
  }, [configurations, selectedBranchId]);

  const fetchConfigurations = useCallback(async () => {
    setLoading(true);
    try {
      const data = await receiptConfigurationService.getAll();
      setConfigurations(data || []);
    } catch (error) {
      toast.error("Failed to load receipt configurations");
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch branch details for receipt preview
  const fetchBranchDetails = useCallback(async () => {
    try {
      const branch = await branchService.getCurrentBranch();
      if (branch) {
        setBranchDetails(branchService.formatBranchForReceipt(branch));
      }
    } catch (error) {
      console.warn("Failed to fetch branch details:", error);
    }
  }, []);

  useEffect(() => {
    fetchConfigurations();
    fetchBranchDetails();
  }, [fetchConfigurations, fetchBranchDetails]);

  useEffect(() => {
    const handleClick = (e) => {
      if (!e.target.closest(".action-menu-container")) {
        setOpenActionMenu(null);
      }
    };
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, []);

  const filteredConfigs = branchConfigs.filter(c => c.receiptType === activeTab);

  // CRUD handlers
  const handleSave = async (data) => {
    try {
      if (editItem) {
        await receiptConfigurationService.update(editItem.id, data);
        toast.success("Configuration updated successfully");
      } else {
        await receiptConfigurationService.create(data);
        toast.success("Configuration created successfully");
      }
      setShowForm(false);
      setEditItem(null);
      fetchConfigurations();
    } catch (error) {
      toast.error("Failed to save configuration");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this configuration?")) {
      try {
        await receiptConfigurationService.delete(id);
        toast.success("Configuration deleted");
        fetchConfigurations();
      } catch (error) {
        toast.error("Failed to delete configuration");
      }
    }
  };

  const handleActivate = async (id) => {
    try {
      await receiptConfigurationService.activate(id);
      toast.success("Configuration activated");
      fetchConfigurations();
    } catch (error) {
      toast.error("Failed to activate configuration");
    }
  };

  const handleDeactivate = async (id) => {
    try {
      await receiptConfigurationService.deactivate(id);
      toast.success("Configuration deactivated");
      fetchConfigurations();
    } catch (error) {
      toast.error("Failed to deactivate configuration");
    }
  };

  const handleSetDefault = async (id) => {
    try {
      await receiptConfigurationService.setDefault(id);
      toast.success("Set as default");
      fetchConfigurations();
    } catch (error) {
      toast.error("Failed to set as default");
    }
  };

  const handleDuplicate = async (id) => {
    try {
      await receiptConfigurationService.duplicate(id);
      toast.success("Configuration duplicated");
      fetchConfigurations();
    } catch (error) {
      toast.error("Failed to duplicate configuration");
    }
  };

  // Actions for header
  const actions = {
    onAdd: () => {
      setEditItem(null);
      setShowForm(true);
    },
    onRefresh: fetchConfigurations,
  };

  // Call renderActions if provided
  React.useEffect(() => {
    if (renderActions) {
      renderActions(actions);
    }
  }, [renderActions]);

  return (
    <div className="p-4">
      <div className="w-full">
        {/* Branch Tabs */}
        {branches.length > 1 && (
          <div className="flex gap-1 bg-white border border-gray-200 rounded-lg p-1 mb-4">
            {branches.map(b => (
              <button
                key={b.id}
                onClick={() => {
                  setSelectedBranchId(b.id);
                  setActiveTab("BILL");
                }}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  selectedBranchId === b.id
                    ? 'bg-teal-600 text-white shadow-sm'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {b.name}
              </button>
            ))}
          </div>
        )}

        {/* Type Tabs */}
        <div className="mb-5">
          <div className="inline-flex bg-slate-100 p-1.5 rounded-2xl">
            <TabButton
              active={activeTab === "BILL"}
              onClick={() => setActiveTab("BILL")}
              icon={Receipt}
              label="Bill Receipts"
              count={branchConfigs.filter(c => c.receiptType === "BILL").length}
              activeClass="bg-white text-blue-700 shadow-lg"
              iconClass="text-blue-600"
              badgeClass="bg-blue-100 text-blue-700"
            />
            <TabButton
              active={activeTab === "KOT"}
              onClick={() => setActiveTab("KOT")}
              icon={UtensilsCrossed}
              label="KOT"
              count={branchConfigs.filter(c => c.receiptType === "KOT").length}
              activeClass="bg-white text-amber-700 shadow-lg"
              iconClass="text-amber-600"
              badgeClass="bg-amber-100 text-amber-700"
            />
            <TabButton
              active={activeTab === "CANCEL_KOT"}
              onClick={() => setActiveTab("CANCEL_KOT")}
              icon={X}
              label="Cancel KOT"
              count={branchConfigs.filter(c => c.receiptType === "CANCEL_KOT").length}
              activeClass="bg-white text-red-700 shadow-lg"
              iconClass="text-red-600"
              badgeClass="bg-red-100 text-red-700"
            />
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="w-12 h-12 border-4 border-slate-200 border-t-teal-600 rounded-full animate-spin"></div>
            <p className="mt-4 text-sm font-medium text-slate-600">Loading templates...</p>
          </div>
        ) : filteredConfigs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 bg-white rounded-xl border border-slate-200">
            <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mb-4">
              <FileText className="w-10 h-10 text-slate-400" />
            </div>
            <p className="text-lg font-semibold text-slate-900">No {activeTab === "BILL" ? "bill" : activeTab === "KOT" ? "KOT" : "Cancel KOT"} templates</p>
            <p className="text-sm text-slate-500 mt-1 mb-4">Create your first template to get started</p>
            <button
              onClick={() => {
                setEditItem(null);
                setShowForm(true);
              }}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center gap-2 text-sm font-medium"
            >
              <Plus size={16} />
              Create Template
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filteredConfigs.map((config) => (
              <PreviewCard
                key={config.id}
                config={config}
                branchDetails={branchDetails}
                onEdit={() => {
                  setEditItem(config);
                  setShowForm(true);
                }}
                onDelete={() => handleDelete(config.id)}
                onActivate={() => handleActivate(config.id)}
                onDeactivate={() => handleDeactivate(config.id)}
                onSetDefault={() => handleSetDefault(config.id)}
                onDuplicate={() => handleDuplicate(config.id)}
                openActionMenu={openActionMenu}
                setOpenActionMenu={setOpenActionMenu}
              />
            ))}
          </div>
        )}

        {/* Results count */}
        {!loading && filteredConfigs.length > 0 && (
          <div className="mt-4 px-2">
            <p className="text-sm text-slate-500">
              {filteredConfigs.length} template{filteredConfigs.length !== 1 ? 's' : ''}
            </p>
          </div>
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <ConfigurationForm
          item={editItem}
          defaultType={activeTab}
          branchDetails={branchDetails}
          onClose={() => {
            setShowForm(false);
            setEditItem(null);
          }}
          onSave={handleSave}
        />
      )}
    </div>
  );
}

// ============================================================
// Preview Card Component - Shows actual receipt preview
// ============================================================
function PreviewCard({
  config,
  branchDetails,
  onEdit,
  onDelete,
  onActivate,
  onDeactivate,
  onSetDefault,
  onDuplicate,
  openActionMenu,
  setOpenActionMenu,
}) {
  const [printing, setPrinting] = useState(false);
  const [showPrinterSelect, setShowPrinterSelect] = useState(false);
  const [printers, setPrinters] = useState([]);
  const isMenuOpen = openActionMenu === config.id;
  const typeConfig = RECEIPT_TYPE_COLORS[config.receiptType];
  const printerType = config.receiptType === "BILL" ? "BILL" : "KOT";

  const handleTestPrint = async (printerId = null) => {
    setPrinting(true);
    setShowPrinterSelect(false);
    try {
      const formattedContent = generateFormattedContent(config, branchDetails);

      // Check if Print Agent is available first
      const isPrintAgentAvailable = await printService.isPrintAgentAvailable();

      if (isPrintAgentAvailable) {
        // Use Print Agent for local printing
        let endpoint = '/print/bill';
        if (config.receiptType === 'KOT') endpoint = '/print/kot';
        else if (config.receiptType === 'CANCEL_KOT') endpoint = '/print/kot/cancel';

        const response = await fetch(`http://localhost:${printService.getPrintAgentPort()}${endpoint}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ content: formattedContent, printerId })
        });
        const result = await response.json();
        if (result.success) {
          toast.success("Print sent successfully!");
        } else {
          toast.error(result.error || result.message || "Print failed");
        }
      } else {
        // Fallback to server printing
        const result = await printService.testPrintTemplate(formattedContent, config.receiptType, printerId);
        if (result.success) {
          toast.success("Print sent successfully!");
        } else {
          toast.error(result.message || "Print failed");
        }
      }
    } catch (error) {
      console.error("Print error:", error);
      toast.error("Failed to print: " + error.message);
    } finally {
      setPrinting(false);
    }
  };

  const handlePrintClick = async (e) => {
    e.stopPropagation();
    try {
      // Check if Print Agent is available
      const isPrintAgentAvailable = await printService.isPrintAgentAvailable();

      if (isPrintAgentAvailable) {
        // Get printers from Print Agent
        const response = await printService.getPrintAgentPrinters();
        const agentPrinters = (response?.printers || []).filter(p => p.type === printerType && p.isActive !== false);
        setPrinters(agentPrinters);

        if (agentPrinters.length === 1) {
          handleTestPrint(agentPrinters[0].id);
        } else if (agentPrinters.length === 0) {
          // No printers configured in Print Agent, try default
          handleTestPrint();
        } else {
          setShowPrinterSelect(true);
        }
      } else {
        // Fallback to server printers
        const data = await printerService.getByType(printerType);
        const activePrinters = data.filter(p => p.isActive);
        setPrinters(activePrinters);

        if (activePrinters.length === 1) {
          handleTestPrint(activePrinters[0].id);
        } else if (activePrinters.length === 0) {
          handleTestPrint();
        } else {
          setShowPrinterSelect(true);
        }
      }
    } catch (error) {
      console.error("Error getting printers:", error);
      handleTestPrint();
    }
  };

  return (
    <div className={`bg-white rounded-2xl border-2 transition-all duration-200 hover:shadow-xl group relative overflow-hidden flex flex-col ${
      config.isActive
        ? "border-slate-200 hover:border-slate-300"
        : "border-slate-100 opacity-60 hover:opacity-100"
    }`}>
      {/* Top Bar with Name and Actions */}
      <div className={`px-3 py-2.5 border-b ${typeConfig.bg} ${typeConfig.border} flex items-center justify-between flex-shrink-0`}>
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {/* Status dot */}
          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${config.isActive ? "bg-emerald-500" : "bg-slate-300"}`} />

          {/* Name */}
          <span className={`font-semibold text-sm truncate ${typeConfig.text}`}>{config.name}</span>

          {/* Default badge */}
          {config.isDefault && (
            <span className="flex-shrink-0 bg-yellow-400 text-yellow-900 text-[9px] font-bold px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
              <Star size={8} fill="currentColor" />
              DEFAULT
            </span>
          )}
        </div>

        {/* Action Menu */}
        <div className="action-menu-container relative flex-shrink-0">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setOpenActionMenu(isMenuOpen ? null : config.id);
            }}
            className="p-1 hover:bg-white/50 rounded-lg transition-colors"
          >
            <MoreVertical size={16} className={typeConfig.text} />
          </button>

          {isMenuOpen && (
            <div className="absolute right-0 top-7 bg-white shadow-xl border border-slate-200 rounded-xl w-44 z-50 overflow-hidden">
              <button
                onClick={() => { onEdit(); setOpenActionMenu(null); }}
                className="w-full text-left px-3 py-2.5 hover:bg-slate-50 text-sm text-slate-700 flex items-center gap-2.5"
              >
                <Edit2 size={14} className="text-slate-400" />
                Edit Template
              </button>
              <button
                onClick={() => { onDuplicate(); setOpenActionMenu(null); }}
                className="w-full text-left px-3 py-2.5 hover:bg-slate-50 text-sm text-slate-700 flex items-center gap-2.5 border-t border-slate-100"
              >
                <Copy size={14} className="text-slate-400" />
                Duplicate
              </button>
              {!config.isDefault && (
                <button
                  onClick={() => { onSetDefault(); setOpenActionMenu(null); }}
                  className="w-full text-left px-3 py-2.5 hover:bg-slate-50 text-sm text-slate-700 flex items-center gap-2.5 border-t border-slate-100"
                >
                  <Star size={14} className="text-slate-400" />
                  Set as Default
                </button>
              )}
              {config.isActive ? (
                <button
                  onClick={() => { onDeactivate(); setOpenActionMenu(null); }}
                  className="w-full text-left px-3 py-2.5 hover:bg-slate-50 text-sm text-slate-700 flex items-center gap-2.5 border-t border-slate-100"
                >
                  <EyeOff size={14} className="text-slate-400" />
                  Deactivate
                </button>
              ) : (
                <button
                  onClick={() => { onActivate(); setOpenActionMenu(null); }}
                  className="w-full text-left px-3 py-2.5 hover:bg-slate-50 text-sm text-slate-700 flex items-center gap-2.5 border-t border-slate-100"
                >
                  <Eye size={14} className="text-slate-400" />
                  Activate
                </button>
              )}
              <button
                onClick={() => { onDelete(); setOpenActionMenu(null); }}
                className="w-full text-left px-3 py-2.5 hover:bg-red-50 text-sm text-red-600 flex items-center gap-2.5 border-t border-slate-100"
              >
                <Trash2 size={14} />
                Delete
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Receipt Preview - Full Size like Live Preview */}
      <div className="py-2 px-1 bg-slate-100 flex justify-center cursor-pointer hover:bg-slate-50 transition-colors flex-1" onClick={onEdit}>
        <ReceiptPreview config={config} branchDetails={branchDetails} />
      </div>

      {/* Bottom Actions - Sticky Footer */}
      <div className="px-3 py-2.5 bg-white border-t border-slate-100 flex items-center justify-between gap-2 flex-shrink-0 mt-auto">
        {/* Paper width badge */}
        <span className="text-[10px] font-medium text-slate-500 bg-slate-100 px-2 py-1 rounded-full">
          {config.paperWidth}
        </span>

        {/* Action buttons */}
        <div className="flex items-center gap-1.5">
          <button
            onClick={onEdit}
            className="px-3 py-1.5 text-xs font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors flex items-center gap-1.5"
          >
            <Settings size={13} />
            Edit
          </button>

          <div className="relative">
            <button
              onClick={handlePrintClick}
              disabled={printing}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all flex items-center gap-1.5 ${
                printing
                  ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                  : "bg-teal-600 text-white hover:bg-teal-700 shadow-sm"
              }`}
            >
              {printing ? <Loader2 size={13} className="animate-spin" /> : <Printer size={13} />}
              {printing ? "..." : "Print"}
            </button>

            {/* Printer Selection Dropdown */}
            {showPrinterSelect && printers.length > 1 && (
              <div className="absolute right-0 bottom-full mb-1 bg-white rounded-lg shadow-xl border border-slate-200 py-1 z-50 min-w-[160px]">
                <div className="px-3 py-1.5 text-[10px] font-semibold text-slate-500 uppercase border-b border-slate-100">
                  Select Printer
                </div>
                {printers.map((printer) => (
                  <button
                    key={printer.id}
                    onClick={(e) => { e.stopPropagation(); handleTestPrint(printer.id); }}
                    className="w-full text-left px-3 py-2 hover:bg-slate-50 text-xs text-slate-700 flex items-center gap-2"
                  >
                    <Printer size={12} className="text-slate-400" />
                    <span className="flex-1 truncate">{printer.name}</span>
                    {printer.isDefault && (
                      <span className="text-[9px] px-1 py-0.5 bg-yellow-100 text-yellow-700 rounded">Default</span>
                    )}
                  </button>
                ))}
                <button
                  onClick={(e) => { e.stopPropagation(); setShowPrinterSelect(false); }}
                  className="w-full text-left px-3 py-1.5 hover:bg-slate-50 text-xs text-slate-400 border-t border-slate-100"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Configuration Form Component with Live Preview
// ============================================================
function ConfigurationForm({ item, defaultType, branchDetails, onClose, onSave }) {
  const [formData, setFormData] = useState(item || { ...DEFAULT_CONFIG, receiptType: defaultType });
  const [activeSection, setActiveSection] = useState("basic");
  const [printing, setPrinting] = useState(false);
  const [printers, setPrinters] = useState([]);
  const [showPrinterSelect, setShowPrinterSelect] = useState(false);

  const printerType = formData.receiptType === "BILL" ? "BILL" : "KOT";

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error("Template name is required");
      return;
    }
    onSave(formData);
  };

  const handleTestPrint = async (printerId = null) => {
    setPrinting(true);
    setShowPrinterSelect(false);
    try {
      const formattedContent = generateFormattedContent(formData, branchDetails);

      // Check if Print Agent is available first
      const isPrintAgentAvailable = await printService.isPrintAgentAvailable();

      if (isPrintAgentAvailable) {
        // Use Print Agent for local printing
        let endpoint = '/print/bill';
        if (formData.receiptType === 'KOT') endpoint = '/print/kot';
        else if (formData.receiptType === 'CANCEL_KOT') endpoint = '/print/kot/cancel';

        const response = await fetch(`http://localhost:${printService.getPrintAgentPort()}${endpoint}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ content: formattedContent, printerId })
        });
        const result = await response.json();
        if (result.success) {
          toast.success("Test print sent!");
        } else {
          toast.error(result.error || result.message || "Print failed");
        }
      } else {
        // Fallback to server printing
        const result = await printService.testPrintTemplate(formattedContent, formData.receiptType, printerId);
        if (result.success) {
          toast.success("Test print sent!");
        } else {
          toast.error(result.message || "Print failed");
        }
      }
    } catch (error) {
      console.error("Print error:", error);
      toast.error("Failed to print: " + error.message);
    } finally {
      setPrinting(false);
    }
  };

  const handlePrintClick = async () => {
    try {
      // Check if Print Agent is available
      const isPrintAgentAvailable = await printService.isPrintAgentAvailable();

      if (isPrintAgentAvailable) {
        // Get printers from Print Agent
        const response = await printService.getPrintAgentPrinters();
        const agentPrinters = (response?.printers || []).filter(p => p.type === printerType && p.isActive !== false);
        setPrinters(agentPrinters);

        if (agentPrinters.length === 1) {
          handleTestPrint(agentPrinters[0].id);
        } else if (agentPrinters.length === 0) {
          handleTestPrint();
        } else {
          setShowPrinterSelect(true);
        }
      } else {
        // Fallback to server printers
        const data = await printerService.getByType(printerType);
        const activePrinters = data.filter(p => p.isActive);
        setPrinters(activePrinters);

        if (activePrinters.length === 1) {
          handleTestPrint(activePrinters[0].id);
        } else if (activePrinters.length === 0) {
          handleTestPrint();
        } else {
          setShowPrinterSelect(true);
        }
      }
    } catch (error) {
      console.error("Error getting printers:", error);
      handleTestPrint();
    }
  };

  const isBill = formData.receiptType === "BILL";
  const isKOT = formData.receiptType === "KOT";
  const isCancelKOT = formData.receiptType === "CANCEL_KOT";
  const typeConfig = RECEIPT_TYPE_COLORS[formData.receiptType];

  const headerBg = isBill ? 'bg-blue-50 border-blue-200' : isKOT ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200';
  const iconBg = isBill ? 'bg-blue-100' : isKOT ? 'bg-amber-100' : 'bg-red-100';
  const iconColor = isBill ? 'text-blue-600' : isKOT ? 'text-amber-600' : 'text-red-600';

  return (
    <div className="fixed inset-0 bg-slate-900/30 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 z-50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className={`${headerBg} border-b px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between`}>
          <div className="flex items-center gap-3">
            <div className={`${iconBg} w-10 h-10 rounded-xl flex items-center justify-center`}>
              {isBill ? <Receipt className={iconColor} size={20} /> : isCancelKOT ? <X className={iconColor} size={20} /> : <UtensilsCrossed className={iconColor} size={20} />}
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">
                {item ? "Edit" : "New"} {isBill ? "Bill" : isKOT ? "KOT" : "Cancel KOT"} Template
              </h2>
              <p className="text-xs text-slate-500">Configure and preview your template</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/50 rounded-xl transition-colors">
            <X size={20} className="text-slate-600" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex">
          {/* Left: Form */}
          <div className="flex-1 overflow-y-auto">
            <form onSubmit={handleSubmit} className="p-4 sm:p-6">
              {/* Section Tabs */}
              <div className="flex flex-wrap gap-1 mb-5 bg-slate-100 p-1 rounded-xl">
                {["basic", "header", "body", "footer", ...((formData.receiptType === "KOT" || formData.receiptType === "CANCEL_KOT") ? ["kot"] : [])].map((section) => (
                  <button
                    key={section}
                    type="button"
                    onClick={() => setActiveSection(section)}
                    className={`px-4 py-2 text-xs font-medium rounded-lg transition-all capitalize ${
                      activeSection === section ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
                    }`}
                  >
                    {section}
                  </button>
                ))}
              </div>

              {/* Basic Section */}
              {activeSection === "basic" && (
                <div className="space-y-4">
                  <SectionTitle icon={FileText} title="Basic Information" />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <FormInput label="Template Name" required value={formData.name} onChange={(v) => handleChange("name", v)} placeholder="e.g., Standard Bill" />
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-1.5">Receipt Type</label>
                      <div className={`px-3 py-2 rounded-lg border text-sm font-medium flex items-center gap-2 ${typeConfig.bg} ${typeConfig.border} ${typeConfig.text}`}>
                        {isBill && <Receipt size={16} />}
                        {isKOT && <UtensilsCrossed size={16} />}
                        {isCancelKOT && <X size={16} />}
                        {typeConfig.label}
                      </div>
                    </div>
                  </div>
                  <FormInput label="Description" value={formData.description} onChange={(v) => handleChange("description", v)} placeholder="Brief description..." />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <FormSelect label="Paper Width" value={formData.paperWidth} onChange={(v) => handleChange("paperWidth", v)} options={[{ value: "80mm", label: "80mm (Standard)" }, { value: "58mm", label: "58mm (Compact)" }]} />
                    <div className="flex items-center gap-6 pt-6">
                      <FormToggle label="Active" checked={formData.isActive} onChange={(v) => handleChange("isActive", v)} />
                      <FormToggle label="Default" checked={formData.isDefault} onChange={(v) => handleChange("isDefault", v)} />
                    </div>
                  </div>
                </div>
              )}

              {/* Header Section */}
              {activeSection === "header" && (
                <div className="space-y-4">
                  <SectionTitle icon={AlignCenter} title="Header Elements" />
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <FormToggle label="Show Logo" checked={formData.showLogo} onChange={(v) => handleChange("showLogo", v)} icon={Building2} />
                    <FormToggle label="Restaurant Name" checked={formData.showRestaurantName} onChange={(v) => handleChange("showRestaurantName", v)} icon={Building2} />
                    <FormToggle label="Address" checked={formData.showAddress} onChange={(v) => handleChange("showAddress", v)} icon={MapPin} />
                    <FormToggle label="Phone" checked={formData.showPhone} onChange={(v) => handleChange("showPhone", v)} icon={Phone} />
                    <FormToggle label="GSTIN" checked={formData.showGstin} onChange={(v) => handleChange("showGstin", v)} icon={Hash} />
                    <FormToggle label="FSSAI" checked={formData.showFssai} onChange={(v) => handleChange("showFssai", v)} icon={Hash} />
                    <FormToggle label="Customer Info" checked={formData.showCustomerInfo} onChange={(v) => handleChange("showCustomerInfo", v)} icon={User} />
                  </div>
                  <FormTextarea label="Custom Header Text" value={formData.headerText} onChange={(v) => handleChange("headerText", v)} placeholder="Additional header text..." />
                </div>
              )}

              {/* Body Section */}
              {activeSection === "body" && (
                <div className="space-y-4">
                  <SectionTitle icon={AlignLeft} title="Item Display" />
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <FormToggle label="Item Code" checked={formData.showItemCode} onChange={(v) => handleChange("showItemCode", v)} icon={Hash} />
                    <FormToggle label="Description" checked={formData.showItemDescription} onChange={(v) => handleChange("showItemDescription", v)} icon={Type} />
                    <FormToggle label="Quantity" checked={formData.showQuantity} onChange={(v) => handleChange("showQuantity", v)} icon={Hash} />
                    <FormToggle label="Unit Price" checked={formData.showUnitPrice} onChange={(v) => handleChange("showUnitPrice", v)} icon={IndianRupee} />
                    <FormToggle label="Item Tax" checked={formData.showItemTax} onChange={(v) => handleChange("showItemTax", v)} icon={BadgePercent} />
                    <FormToggle label="Item Discount" checked={formData.showItemDiscount} onChange={(v) => handleChange("showItemDiscount", v)} icon={BadgePercent} />
                  </div>
                </div>
              )}

              {/* Footer Section */}
              {activeSection === "footer" && (
                <div className="space-y-4">
                  <SectionTitle icon={MessageSquare} title="Footer Elements" />
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <FormToggle label="Subtotal" checked={formData.showSubtotal} onChange={(v) => handleChange("showSubtotal", v)} icon={IndianRupee} />
                    <FormToggle label="Tax Breakdown" checked={formData.showTaxBreakdown} onChange={(v) => handleChange("showTaxBreakdown", v)} icon={BadgePercent} />
                    <FormToggle label="Discount Total" checked={formData.showDiscountTotal} onChange={(v) => handleChange("showDiscountTotal", v)} icon={BadgePercent} />
                    <FormToggle label="Grand Total" checked={formData.showGrandTotal} onChange={(v) => handleChange("showGrandTotal", v)} icon={IndianRupee} />
                    <FormToggle label="Payment Method" checked={formData.showPaymentMethod} onChange={(v) => handleChange("showPaymentMethod", v)} icon={CreditCard} />
                    <FormToggle label="QR Code" checked={formData.showQrCode} onChange={(v) => handleChange("showQrCode", v)} icon={QrCode} />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <FormToggle label="Thank You Message" checked={formData.showThankYou} onChange={(v) => handleChange("showThankYou", v)} icon={MessageSquare} />
                      {formData.showThankYou && (
                        <FormInput className="mt-2" value={formData.thankYouText} onChange={(v) => handleChange("thankYouText", v)} placeholder="Thank you message..." />
                      )}
                    </div>
                    {formData.showQrCode && (
                      <FormInput label="QR Code Data" value={formData.qrCodeData} onChange={(v) => handleChange("qrCodeData", v)} placeholder="UPI ID or URL..." />
                    )}
                  </div>
                  <FormTextarea label="Custom Footer Text" value={formData.footerText} onChange={(v) => handleChange("footerText", v)} placeholder="Additional footer..." />
                </div>
              )}

              {/* KOT Section */}
              {activeSection === "kot" && (formData.receiptType === "KOT" || formData.receiptType === "CANCEL_KOT") && (
                <div className="space-y-4">
                  <SectionTitle icon={ChefHat} title="KOT Options" />
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <FormToggle label="Order Number" checked={formData.showOrderNumber} onChange={(v) => handleChange("showOrderNumber", v)} icon={Hash} />
                    <FormToggle label="Table Number" checked={formData.showTableNumber} onChange={(v) => handleChange("showTableNumber", v)} icon={Utensils} />
                    <FormToggle label="Waiter Name" checked={formData.showWaiterName} onChange={(v) => handleChange("showWaiterName", v)} icon={User} />
                    <FormToggle label="Timestamp" checked={formData.showTimestamp} onChange={(v) => handleChange("showTimestamp", v)} icon={Clock} />
                    <FormToggle label="Kitchen Station" checked={formData.showKitchenStation} onChange={(v) => handleChange("showKitchenStation", v)} icon={ChefHat} />
                    <FormToggle label="Special Instructions" checked={formData.showSpecialInstructions} onChange={(v) => handleChange("showSpecialInstructions", v)} icon={MessageSquare} />
                  </div>
                  <FormSelect label="Font Size" value={formData.kotFontSize} onChange={(v) => handleChange("kotFontSize", v)} options={[{ value: "small", label: "Small" }, { value: "normal", label: "Normal" }, { value: "large", label: "Large" }]} />
                </div>
              )}

              {/* Save Button */}
              <div className="mt-6 pt-4 border-t flex justify-end gap-3">
                <button type="button" onClick={onClose} className="px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50">
                  Cancel
                </button>
                <button type="submit" className="px-5 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 flex items-center gap-2 shadow-sm">
                  <Save size={16} />
                  {item ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>

          {/* Right: Live Preview */}
          <div className="w-80 lg:w-96 bg-slate-100 p-4 overflow-y-auto hidden md:flex flex-col border-l">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Live Preview</h3>
              <div className="relative">
                <button
                  onClick={handlePrintClick}
                  disabled={printing}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    printing ? "bg-slate-200 text-slate-400" : "bg-teal-600 text-white hover:bg-teal-700 shadow-sm"
                  }`}
                >
                  {printing ? <Loader2 size={14} className="animate-spin" /> : <Printer size={14} />}
                  {printing ? "Printing..." : "Test Print"}
                </button>

                {showPrinterSelect && printers.length > 1 && (
                  <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-xl border py-1 z-50 min-w-[160px]">
                    <div className="px-3 py-1.5 text-[10px] font-semibold text-slate-500 uppercase border-b">Select Printer</div>
                    {printers.map((printer) => (
                      <button key={printer.id} onClick={() => handleTestPrint(printer.id)} className="w-full text-left px-3 py-2 hover:bg-slate-50 text-xs flex items-center gap-2">
                        <Printer size={12} className="text-slate-400" />
                        <span className="flex-1">{printer.name}</span>
                      </button>
                    ))}
                    <button onClick={() => setShowPrinterSelect(false)} className="w-full text-left px-3 py-1.5 hover:bg-slate-50 text-xs text-slate-400 border-t">Cancel</button>
                  </div>
                )}
              </div>
            </div>
            <div className="flex-1 flex items-start justify-center">
              <ReceiptPreview config={formData} branchDetails={branchDetails} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Full Receipt Preview Component (for modal)
// ============================================================
function ReceiptPreview({ config, branchDetails }) {
  const isBill = config.receiptType === "BILL";
  const isKOT = config.receiptType === "KOT" || config.receiptType === "CANCEL_KOT";
  const isCancelKOT = config.receiptType === "CANCEL_KOT";
  const fontSize = config.kotFontSize === "large" ? "text-sm" : config.kotFontSize === "small" ? "text-[9px]" : "text-[10px]";

  return (
    <div className={`bg-white rounded-lg shadow-xl p-4 font-mono ${fontSize} ${config.paperWidth === "58mm" ? "w-[200px]" : "w-[280px]"}`}>
      {/* Header */}
      <div className="text-center border-b border-dashed border-slate-300 pb-2 mb-2">
        {config.showLogo && branchDetails?.logoUrl ? (
          <img src={branchDetails.logoUrl} alt="Logo" className="mx-auto mb-1" style={{ maxHeight: '40px', maxWidth: '100px' }} />
        ) : config.showLogo && (
          <div className="w-10 h-10 bg-slate-200 rounded-full mx-auto mb-1 flex items-center justify-center">
            <Building2 size={20} className="text-slate-400" />
          </div>
        )}
        {config.showRestaurantName && <div className="font-bold text-sm">{branchDetails?.name || 'MAVERICK DINE'}</div>}
        {config.showAddress && branchDetails?.address && <div className="text-[9px] text-slate-600">{branchDetails.address}</div>}
        {config.showPhone && branchDetails?.phone && <div className="text-[9px] text-slate-600">Ph: {branchDetails.phone}</div>}
        {config.showGstin && branchDetails?.gstNumber && <div className="text-[9px] text-slate-500">GSTIN: {branchDetails.gstNumber}</div>}
        {config.showFssai && branchDetails?.fssaiNumber && <div className="text-[9px] text-slate-500">FSSAI: {branchDetails.fssaiNumber}</div>}
        {config.headerText && <div className="text-[9px] text-slate-600 mt-1">{config.headerText}</div>}
      </div>

      {isKOT && (
        <div className="text-center mb-2">
          <div className={`font-bold text-lg ${isCancelKOT ? "text-red-600" : ""}`}>{isCancelKOT ? "CANCEL KOT" : "KOT"}</div>
          {isCancelKOT && <div className="text-[9px] text-red-500 font-medium border border-red-300 bg-red-50 rounded px-1 py-0.5 inline-block mb-1">*** CANCELLED ***</div>}
          {config.showOrderNumber && <div>Order #: KOT-001</div>}
          {config.showTableNumber && <div>Table: T5-A</div>}
          {config.showWaiterName && <div>Waiter: John</div>}
          {config.showTimestamp && <div className="text-[9px]">{new Date().toLocaleString()}</div>}
          {config.showKitchenStation && <div className="font-semibold mt-1">Station: MAIN KITCHEN</div>}
        </div>
      )}

      {isBill && (
        <div className="text-center text-[9px] mb-2">
          <div>Bill #: INV-2024-001</div>
          <div>Table: T5 | Date: {new Date().toLocaleDateString()}</div>
          {config.showCustomerInfo && <div className="text-left mt-1 border-t border-dashed border-slate-200 pt-1"><span className="font-semibold">Customer:</span> John Doe | 98765xxxxx</div>}
        </div>
      )}

      <div className="border-t border-b border-dashed border-slate-300 py-2 my-2">
        <div className="flex justify-between text-[9px] font-semibold mb-1 text-slate-600">
          <span>Item</span>
          {config.showQuantity && <span>Qty</span>}
          {config.showUnitPrice && isBill && <span>Amt</span>}
        </div>
        {[{ name: "Chicken Biryani", qty: 2, price: 350 }, { name: "Butter Naan", qty: 4, price: 40 }, { name: "Paneer Tikka", qty: 1, price: 280 }].map((item, idx) => (
          <div key={idx} className="flex justify-between py-0.5">
            <span className="flex-1">{config.showItemCode && <span className="text-slate-400">#00{idx + 1} </span>}{item.name}</span>
            {config.showQuantity && <span className="w-8 text-center">{item.qty}</span>}
            {config.showUnitPrice && isBill && <span className="w-12 text-right">{item.qty * item.price}</span>}
          </div>
        ))}
        {config.showSpecialInstructions && isKOT && (
          <div className="mt-2 pt-2 border-t border-dashed border-slate-200 text-[9px] italic text-slate-600">
            {isCancelKOT ? "Reason: Customer changed mind" : "Note: Extra spicy, no onions"}
          </div>
        )}
      </div>

      {isBill && (
        <div className="space-y-1 text-[10px]">
          {config.showSubtotal && <div className="flex justify-between"><span>Subtotal</span><span>1,140.00</span></div>}
          {config.showDiscountTotal && <div className="flex justify-between text-green-600"><span>Discount (10%)</span><span>-114.00</span></div>}
          {config.showTaxBreakdown && (
            <>
              <div className="flex justify-between text-slate-600"><span>CGST (2.5%)</span><span>25.65</span></div>
              <div className="flex justify-between text-slate-600"><span>SGST (2.5%)</span><span>25.65</span></div>
            </>
          )}
          {config.showGrandTotal && <div className="flex justify-between font-bold text-sm border-t border-dashed pt-1 mt-1"><span>GRAND TOTAL</span><span>1,077.30</span></div>}
          {config.showPaymentMethod && <div className="flex justify-between text-slate-600 pt-1"><span>Payment</span><span>CASH</span></div>}
        </div>
      )}

      {config.showQrCode && isBill && (
        <div className="text-center mt-3 pt-2 border-t border-dashed">
          <div className="w-16 h-16 bg-slate-200 mx-auto flex items-center justify-center"><QrCode size={40} className="text-slate-400" /></div>
          <div className="text-[8px] text-slate-500 mt-1">Scan to Pay</div>
        </div>
      )}

      {config.showThankYou && isBill && (
        <div className="text-center mt-2 pt-2 border-t border-dashed"><div className="text-[10px] font-medium">{config.thankYouText || branchDetails?.thankYouMessage || "Thank you! Visit Again!"}</div></div>
      )}

      {config.footerText && <div className="text-center text-[8px] text-slate-500 mt-2">{config.footerText}</div>}
    </div>
  );
}

// ============================================================
// Form Components
// ============================================================
function SectionTitle({ icon: Icon, title }) {
  return (
    <div className="flex items-center gap-2 pb-2 border-b border-slate-200 mb-3">
      <Icon size={16} className="text-slate-500" />
      <h3 className="text-sm font-semibold text-slate-700">{title}</h3>
    </div>
  );
}

function FormInput({ label, required, value, onChange, placeholder, className = "" }) {
  return (
    <div className={className}>
      {label && <label className="block text-xs font-medium text-slate-700 mb-1.5">{label} {required && <span className="text-red-500">*</span>}</label>}
      <input type="text" value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-teal-400 focus:ring-1 focus:ring-teal-100" />
    </div>
  );
}

function FormTextarea({ label, value, onChange, placeholder }) {
  return (
    <div>
      {label && <label className="block text-xs font-medium text-slate-700 mb-1.5">{label}</label>}
      <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} rows={2} className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-teal-400 focus:ring-1 focus:ring-teal-100 resize-none" />
    </div>
  );
}

function FormSelect({ label, value, onChange, options }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-700 mb-1.5">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-teal-400 focus:ring-1 focus:ring-teal-100">
        {options.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
      </select>
    </div>
  );
}

function FormToggle({ label, checked, onChange, icon: Icon }) {
  return (
    <label className="flex items-center gap-2.5 p-2.5 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors border border-transparent hover:border-slate-200">
      <input type="checkbox" checked={checked || false} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500" />
      {Icon && <Icon size={14} className="text-slate-400" />}
      <span className="text-xs font-medium text-slate-700">{label}</span>
    </label>
  );
}
